import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import { CONVERSATION_FACTS_DIMENSIONS, ROUTING_MEASURES } from 'cube/constants'
import { ResponseRateQuery } from 'cube/queries'
import { isNil } from 'ramda'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import {
  type RoutingKPIMetric,
  resolveCubeKPIPercentage,
  resolveKPIScore,
  resolveTotalRecords,
} from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

import { KPI_SCORES } from './ResponseRate.constants'

/***
 * Hook that cube to fetch response rate data
 *
 * @returns {RoutingKPIMetric}
 */
export const useResponseRate = (): RoutingKPIMetric => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()

  const cubeQuery = new CubeQueryBuilder({ query: ResponseRateQuery })
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withFilters(pageFilters)
    .build()

  const { data, isLoading, isError } = useCubeQuery({
    query: cubeQuery,
  })

  const responseRate = useMemo(
    () =>
      resolveCubeKPIPercentage(
        data,
        ROUTING_MEASURES.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S,
        ROUTING_MEASURES.ROUTING_REQUEST_TO_TEAMMATE_ROUTED_IN
      ),
    [data]
  )

  const totalRecords = useMemo(
    () =>
      resolveTotalRecords(
        data,
        ROUTING_MEASURES.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S
      ),
    [data]
  )

  const totalRequests = useMemo(
    () =>
      resolveTotalRecords(
        data,
        ROUTING_MEASURES.ROUTING_REQUEST_TO_TEAMMATE_ROUTED_IN
      ),
    [data]
  )

  const score = useMemo(
    () => resolveKPIScore(responseRate, KPI_SCORES),
    [responseRate]
  )

  const metric = useMemo(() => responseRate ?? null, [responseRate])

  const hasNoData = useMemo(
    () => !isLoading && isNil(responseRate),
    [isLoading, responseRate]
  )

  return useMemo(
    () => ({
      score,
      metric,
      isLoading,
      isError,
      hasNoData,
      totalRecords,
      totalRequests,
    }),
    [score, metric, isLoading, isError, hasNoData, totalRecords, totalRequests]
  )
}

export default useResponseRate
